import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Copy, Trash2, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { useState } from "react";
import { cn } from "@/lib/utils";

interface BulkActionsBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onBulkReschedule: (date: Date) => void;
  onBulkDelete: () => void;
}

export const BulkActionsBar = ({
  selectedCount,
  onClearSelection,
  onBulkReschedule,
  onBulkDelete,
}: BulkActionsBarProps) => {
  const [rescheduleDate, setRescheduleDate] = useState<Date>();

  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom">
      <div className="bg-primary text-primary-foreground rounded-full px-6 py-3 shadow-lg flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="bg-white/20 text-white">
            {selectedCount}
          </Badge>
          <span className="text-sm font-medium">
            {selectedCount === 1 ? "post selected" : "posts selected"}
          </span>
        </div>

        <div className="h-6 w-px bg-white/20" />

        {/* Reschedule */}
        <Popover>
          <PopoverTrigger asChild>
            <Button 
              size="sm" 
              variant="ghost" 
              className="text-white hover:bg-white/10"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Reschedule
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="center">
            <CalendarComponent
              mode="single"
              selected={rescheduleDate}
              onSelect={(date) => {
                if (date) {
                  setRescheduleDate(date);
                  onBulkReschedule(date);
                }
              }}
              disabled={(date) => date < new Date()}
              initialFocus
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>

        {/* Delete */}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button 
              size="sm" 
              variant="ghost" 
              className="text-white hover:bg-white/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {selectedCount} post(s)?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. The selected posts will be permanently deleted.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={onBulkDelete}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Clear Selection */}
        <Button 
          size="sm" 
          variant="ghost" 
          className="text-white hover:bg-white/10"
          onClick={onClearSelection}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
